import { useMemo } from 'react';
import { encode } from 'uqr';
import { cn } from '../lib/cn';

export interface QrCodeProps {
  /** Content of the code, e.g. the `otpauth://` link of an authenticator app. */
  value: string;
  /** What the code is for, in words. Never the content: it may be a secret. */
  label: string;
  className?: string;
}

/**
 * QR code as SVG with the quiet zone of four modules. Dark on light in both themes, because not
 * every scanner reads inverted codes.
 */
export function QrCode({ value, label, className }: QrCodeProps) {
  const { path, size } = useMemo(() => {
    const qr = encode(value, { ecc: 'M', border: 4 });
    // One rectangle per run of dark modules keeps the path short.
    const runs: string[] = [];
    qr.data.forEach((row, y) => {
      for (let x = 0; x < row.length; x++) {
        if (!row[x]) continue;
        const start = x;
        while (row[x + 1]) x++;
        runs.push(`M${start} ${y}h${x - start + 1}v1H${start}z`);
      }
    });
    return { path: runs.join(''), size: qr.size };
  }, [value]);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
      className={cn('block aspect-square bg-qr-light', className)}
    >
      <path d={path} className="fill-qr-dark" />
    </svg>
  );
}
